import React, { useState } from 'react';
import {
   Dialog,
   DialogTitle,
   DialogContent,
   DialogActions,
   Typography,
   TextField,
   Button,
   Box,
   Alert,
   CircularProgress,
   List,
   ListItem,
   ListItemIcon,
   ListItemText,
   Divider,
} from '@mui/material';
import {
   Email as EmailIcon,
   Phone as PhoneIcon,
   Link as LinkIcon,
   PersonAdd as PersonAddIcon,
} from '@mui/icons-material';

interface PotentialAccount {
   email?: string;
   phoneNumber?: string;
   fullName: string;
}

interface AccountLinkingDialogProps {
   open: boolean;
   onClose: () => void;
   potentialAccounts: PotentialAccount[];
   phoneNumber?: string;
   email?: string;
   signupType: 'phone' | 'email';
   onLinkAccount: (email: string, password: string) => Promise<void>;
   onCreateNewAccount: () => Promise<void>;
   loading?: boolean;
   error?: string | null;
}

const AccountLinkingDialog: React.FC<AccountLinkingDialogProps> = ({
   open,
   onClose,
   potentialAccounts,
   phoneNumber,
   email,
   signupType,
   onLinkAccount,
   onCreateNewAccount,
   loading = false,
   error = null,
}) => {
   const [selectedAccount, setSelectedAccount] = useState<string>('');
   const [password, setPassword] = useState<string>('');
   const [showLinkForm, setShowLinkForm] = useState(false);

   const handleLinkAccount = async () => {
      if (!selectedAccount || !password) return;
      await onLinkAccount(selectedAccount, password);
   };

   const handleSelectAccount = (accountIdentifier: string) => {
      setSelectedAccount(accountIdentifier);
      setShowLinkForm(true);
   };

   const isPhoneToEmail = signupType === 'phone';
   const accountDisplayField = isPhoneToEmail ? 'email' : 'phoneNumber';
   const linkingValue = isPhoneToEmail ? phoneNumber : email;

   return (
      <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
         <DialogTitle>
            <Box display='flex' alignItems='center' gap={1}>
               <LinkIcon color='primary' />
               <Typography variant='h6'>Link Your Accounts</Typography>
            </Box>
         </DialogTitle>

         <DialogContent>
            <Alert severity='info' sx={{ mb: 3 }}>
               We found existing accounts with the same name. You can link your{' '}
               {isPhoneToEmail ? 'phone number' : 'email'} to an existing account or create a new
               one.
            </Alert>

            {error && (
               <Alert severity='error' sx={{ mb: 2 }}>
                  {error}
               </Alert>
            )}

            <Box sx={{ mb: 3 }}>
               <Typography variant='body2' color='text.secondary' gutterBottom>
                  {isPhoneToEmail ? 'Phone number' : 'Email'} to be linked:
               </Typography>
               <Box display='flex' alignItems='center' gap={1}>
                  {isPhoneToEmail ? (
                     <PhoneIcon color='primary' fontSize='small' />
                  ) : (
                     <EmailIcon color='primary' fontSize='small' />
                  )}
                  <Typography variant='body1' fontWeight='medium'>
                     {linkingValue}
                  </Typography>
               </Box>
            </Box>

            {!showLinkForm ? (
               <>
                  <Typography variant='subtitle1' gutterBottom>
                     Existing Accounts Found:
                  </Typography>

                  <List>
                     {potentialAccounts.map((account, index) => {
                        const accountValue = isPhoneToEmail ? account.email : account.phoneNumber;
                        return (
                           <ListItem
                              key={index}
                              button
                              onClick={() => handleSelectAccount(accountValue!)}
                              sx={{
                                 border: '1px solid',
                                 borderColor: 'grey.300',
                                 borderRadius: 1,
                                 mb: 1,
                                 '&:hover': {
                                    borderColor: 'primary.main',
                                    bgcolor: 'primary.50',
                                 },
                              }}
                           >
                              <ListItemIcon>
                                 {isPhoneToEmail ? (
                                    <EmailIcon color='primary' />
                                 ) : (
                                    <PhoneIcon color='primary' />
                                 )}
                              </ListItemIcon>
                              <ListItemText
                                 primary={accountValue}
                                 secondary={`Name: ${account.fullName}`}
                              />
                           </ListItem>
                        );
                     })}
                  </List>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant='body2' color='text.secondary' gutterBottom>
                     Or create a new account instead:
                  </Typography>

                  <Button
                     variant='outlined'
                     startIcon={<PersonAddIcon />}
                     onClick={onCreateNewAccount}
                     disabled={loading}
                     fullWidth
                     sx={{ mt: 1 }}
                  >
                     Create New Account
                  </Button>
               </>
            ) : (
               <Box>
                  <Typography variant='subtitle1' gutterBottom>
                     Link to: {selectedAccount}
                  </Typography>

                  <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                     Please enter your password to confirm account linking:
                  </Typography>

                  <TextField
                     fullWidth
                     type='password'
                     label='Password'
                     value={password}
                     onChange={(e) => setPassword(e.target.value)}
                     margin='normal'
                     required
                     autoFocus
                  />

                  <Box display='flex' gap={2} mt={2}>
                     <Button
                        variant='outlined'
                        onClick={() => {
                           setShowLinkForm(false);
                           setSelectedAccount('');
                           setPassword('');
                        }}
                        disabled={loading}
                     >
                        Back
                     </Button>
                     <Button
                        variant='contained'
                        onClick={handleLinkAccount}
                        disabled={loading || !password}
                        startIcon={loading ? <CircularProgress size={20} /> : <LinkIcon />}
                        sx={{ flex: 1 }}
                     >
                        {loading ? 'Linking...' : 'Link Accounts'}
                     </Button>
                  </Box>
               </Box>
            )}
         </DialogContent>

         {!showLinkForm && (
            <DialogActions>
               <Button onClick={onClose} disabled={loading}>
                  Cancel
               </Button>
            </DialogActions>
         )}
      </Dialog>
   );
};

export default AccountLinkingDialog;
